import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, PRODUCT_NAV } from '../../data/categoryNav';
type NavItem = {
  label: string;
  fullLabel: string;
  href: string;
  items?: NavLink[];
  /** Product groups render the full-width nested mega panel. */
  mega?: boolean;
  alignRight?: boolean;
  highlight?: boolean;
};
const SERVICES_ITEMS: NavLink[] = [{
  label: 'Services',
  href: '/Services.html'
}, {
  label: 'Install Instructions',
  href: '/13.html'
}, {
  label: 'USPS Resources',
  href: '/USPSResources.html'
}, {
  label: 'Catalog',
  href: '/29.html'
}, {
  label: 'Product Option Patterns',
  href: '/product-option-patterns.html'
}, {
  label: 'Our Policies',
  href: '/OurPolicies'
}, {
  label: 'Dominion Valley Community',
  href: '/3948.html'
}];
const ABOUT_ITEMS: NavLink[] = [{
  label: 'About Us',
  href: '/AboutMidAtlanticMailboxInc.html'
}, {
  label: 'Our Gallery',
  href: '/3949.html'
}];
const NAV_ITEMS: NavItem[] = [...PRODUCT_NAV.map(group => ({
  label: group.label,
  fullLabel: group.fullLabel,
  href: group.href,
  items: group.items,
  mega: true
})), {
  label: 'Services & Resources',
  fullLabel: 'Services & Resources',
  href: '/Services.html',
  items: SERVICES_ITEMS,
  alignRight: true
}, {
  label: 'About',
  fullLabel: 'About',
  href: '/AboutMidAtlanticMailboxInc.html',
  items: ABOUT_ITEMS,
  alignRight: true
}, {
  label: 'Help Choosing',
  fullLabel: 'Help Choosing',
  href: '/ContactUs.html',
  highlight: true
}];
const MOBILE_GROUPS: NavItem[] = [...NAV_ITEMS.filter(item => item.items), {
  label: 'Help Choosing',
  fullLabel: 'Help Choosing',
  href: '/ContactUs.html',
  items: [{
    label: 'Contact / FAQs',
    href: '/ContactUs.html'
  }]
}];
export function Navigation() {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>('Mailboxes');
  const [openSubGroup, setOpenSubGroup] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setActiveMenu(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const openMenu = (item: NavItem) => {
    setActiveMenu(item.label);
    if (item.mega) {
      const firstWithChildren = item.items?.find(i => i.children?.length);
      setActiveSub(firstWithChildren?.label ?? null);
    } else {
      setActiveSub(null);
    }
  };
  const closeMenus = () => {
    setActiveMenu(null);
    setActiveSub(null);
  };
  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      closeMenus();
    }
  };
  const activeItem = NAV_ITEMS.find(i => i.label === activeMenu);
  const activeChildren = activeItem?.items?.find(i => i.label === activeSub)?.children ?? [];
  return <>
      {/* Mobile menu trigger bar */}
      <div className="lg:hidden bg-brand-charcoal text-white px-4 py-3 flex justify-between items-center relative z-40" data-id="element-588" data-mp="2jn3z014b5daq">
        <span className="font-serif font-bold" data-id="element-589" data-mp="2ep2h3jimbk0u">Shop Categories</span>
        <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-1" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} data-id="element-590" data-mp="2tljq443c4mk8">
          {mobileMenuOpen ? <X size={24} data-id="element-591" data-mp="8trgktqjajgh" /> : <Menu size={24} data-id="element-592" data-mp="8xf7rm8iats1" />}
        </button>
      </div>

      {/* Mobile Drawer (nested accordions) */}
      <AnimatePresence data-id="element-593" data-mp="3yketrgtrou7">
        {mobileMenuOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="lg:hidden bg-brand-charcoal text-white overflow-hidden absolute w-full max-w-full z-30 shadow-xl" data-id="element-594" data-mp="29ljpksdgh9db">
            <div className="max-h-[75vh] overflow-y-auto py-2" data-id="element-595" data-mp="2sj471edw55nw">
              {MOBILE_GROUPS.map(group => {
            const isOpen = openGroup === group.label;
            return <div key={group.label} className="border-b border-white/10 last:border-0" data-id="element-596" data-mp="329z47dsodpo4">
                    <button type="button" onClick={() => setOpenGroup(isOpen ? null : group.label)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left text-base font-semibold focus:outline-none focus:bg-white/10" aria-expanded={isOpen} data-id="element-597" data-mp="3badqoghdlat7">
                      <span className="min-w-0" data-id="element-598" data-mp="3geppniq1bv69">{group.fullLabel}</span>
                      <ChevronDown size={18} className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-599" data-mp="3gezf957640bf" />
                    </button>
                    <AnimatePresence data-id="element-600" data-mp="cztnf5dletlf">
                      {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-white/5" data-id="element-601" data-mp="7zo3ychh8def">
                          <ul data-id="element-602" data-mp="2q4ys7y7n5p26">
                            {group.mega && <li data-id="element-603" data-mp="2bg2mw8wyyq59">
                                <a href={group.href} className="block pl-8 pr-5 py-3 text-sm font-semibold text-brand-gold hover:text-white focus:outline-none focus:text-white" data-id="element-604" data-mp="2bf193wokjpju">
                                  All {group.fullLabel}
                                </a>
                              </li>}
                            {group.items?.map(item => {
                      const subKey = `${group.label}-${item.label}`;
                      const subOpen = openSubGroup === subKey;
                      return <li key={subKey} data-id="element-605" data-mp="3ruavt0oth0yf">
                                  <div className="flex items-stretch" data-id="element-606" data-mp="3rrybwhzi0giq">
                                    <a href={item.href} className="flex-1 min-w-0 block pl-8 pr-3 py-3.5 text-sm text-[#E2DFDD] hover:text-brand-gold hover:bg-white/5 focus:outline-none focus:text-brand-gold" data-id="element-607" data-mp="1g78p3h9jj1zj">
                                      {item.label}
                                    </a>
                                    {item.children && <button type="button" onClick={() => setOpenSubGroup(subOpen ? null : subKey)} className="px-4 text-[#E2DFDD] hover:text-brand-gold focus:outline-none focus:text-brand-gold" aria-expanded={subOpen} aria-label={`${subOpen ? 'Hide' : 'Show'} ${item.label} subcategories`} data-id="element-608" data-mp="1uwd6wxcfb474">
                                        <ChevronDown size={16} className={`transition-transform ${subOpen ? 'rotate-180' : ''}`} data-id="element-609" data-mp="3tllk5i6dcjeu" />
                                      </button>}
                                  </div>
                                  {item.children && subOpen && <ul className="bg-black/20 pb-1" data-id="element-610" data-mp="3fsvxrcmxhch7">
                                      {item.children.map(child => <li key={`${subKey}-${child.label}`} data-id="element-611" data-mp="q2ilnw7ekdlu">
                                          <a href={child.href} className="block pl-12 pr-5 py-2.5 text-[13px] text-[#CFCBC6] hover:text-brand-gold focus:outline-none focus:text-brand-gold" data-id="element-612" data-mp="v256orth2y57">
                                            {child.label}
                                          </a>
                                          {child.children && <ul data-id="element-613" data-mp="1vwgi7ifp9x5p">
                                              {child.children.map(grandChild => <li key={`${subKey}-${child.label}-${grandChild.label}`} data-id="element-614" data-mp="1mw0fib6s6u1w">
                                                    <a href={grandChild.href} className="block pl-16 pr-5 py-2 text-[13px] text-[#B9B4AE] hover:text-brand-gold focus:outline-none focus:text-brand-gold" data-id="element-615" data-mp="3h9t1h467kbqs">
                                                      {grandChild.label}
                                                    </a>
                                                  </li>)}
                                            </ul>}
                                        </li>)}
                                    </ul>}
                                </li>;
                    })}
                          </ul>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Desktop Nav — constrained to the same content rails as the header */}
      <nav className="hidden lg:block bg-brand-charcoal text-white relative z-30" data-id="element-616" data-mp="w1n552vohehm">
        <div className="max-w-7xl mx-auto px-4 relative" onMouseLeave={closeMenus} onBlur={handleBlur} data-id="element-617" data-mp="hc7ero027b2g">
          <ul className="flex items-center flex-nowrap w-full justify-between gap-x-0 xl:gap-x-1 text-[11.5px] xl:text-[13px] 2xl:text-sm" data-id="element-618" data-mp="mb1xyr1xv1n3">
            {NAV_ITEMS.map(item => {
            const hasMenu = Boolean(item.items);
            return <li key={item.label} className="relative" onMouseEnter={() => openMenu(item)} onFocus={() => openMenu(item)} data-id="element-619" data-mp="102oy51z04ub5">
                  <a href={item.href} className={`flex items-center gap-0.5 px-1.5 xl:px-2.5 2xl:px-3 py-3.5 font-medium transition-colors focus:outline-none focus:bg-white/20 whitespace-nowrap ${item.highlight ? 'bg-brand-maroon hover:bg-brand-maroonDark rounded-sm' : 'hover:bg-white/10'}`} data-id="element-620" data-mp="3krcu4ocg6pbk">
                    {item.label}
                    {hasMenu && <ChevronDown size={12} className={`shrink-0 transition-transform ${activeMenu === item.label ? 'rotate-180' : ''}`} data-id="element-621" data-mp="1mh4blcn1mhqw" />}
                  </a>

                  {/* Simple dropdown for the non-product menus */}
                  {item.items && !item.mega && <AnimatePresence data-id="element-622" data-mp="13l5urrzfg5z6">
                      {activeMenu === item.label && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.15
                }} className={`absolute top-full bg-white text-brand-charcoal shadow-xl border border-brand-border rounded-b-md overflow-hidden z-40 w-80 ${item.alignRight ? 'right-0' : 'left-0'}`} data-id="element-623" data-mp="ylt56zh68ksw">
                          <div className="px-5 pt-4 pb-3 border-b border-brand-border/60" data-id="element-624" data-mp="2mjtzyloeoqhr">
                            <a href={item.href} className="block text-sm font-serif font-bold text-brand-charcoal hover:text-brand-maroon transition-colors focus:outline-none focus:text-brand-maroon" data-id="element-625" data-mp="2rhm208irlszo">
                              {item.fullLabel}
                            </a>
                          </div>
                          <ul className="py-2" data-id="element-626" data-mp="9baxgxwn29zw">
                            {item.items.map(subItem => <li key={`${item.label}-${subItem.label}`} data-id="element-627" data-mp="5biv52xu902z">
                                <a href={subItem.href} className="block px-5 py-2.5 text-sm text-brand-muted hover:bg-brand-panel hover:text-brand-maroon transition-colors focus:outline-none focus:bg-brand-panel focus:text-brand-maroon" data-id="element-628" data-mp="jarzn5com5r4">
                                  {subItem.label}
                                </a>
                              </li>)}
                          </ul>
                        </motion.div>}
                    </AnimatePresence>}
                </li>;
          })}
          </ul>

          {/* Nested mega panel for product categories — spans the content rails
              so nested lists can never run off-screen. */}
          <AnimatePresence data-id="element-629" data-mp="1qm4obduszsgc">
            {activeItem?.mega && activeItem.items && <motion.div key={activeItem.label} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.15
          }} className="absolute left-4 right-4 top-full bg-white text-brand-charcoal shadow-xl border border-brand-border rounded-b-md overflow-hidden z-40" data-id="element-630" data-mp="1um6sbto0gxys">
                <div className="px-6 pt-4 pb-3 border-b border-brand-border/60" data-id="element-631" data-mp="12wad10q4uby7">
                  <a href={activeItem.href} className="text-sm font-serif font-bold text-brand-charcoal hover:text-brand-maroon transition-colors focus:outline-none focus:text-brand-maroon" data-id="element-632" data-mp="o02s1y6wh6hv">
                    All {activeItem.fullLabel}
                  </a>
                </div>
                <div className="grid grid-cols-[minmax(0,20rem)_minmax(0,1fr)]" data-id="element-633" data-mp="50ogxr2sw0jk">
                  <ul className="py-2 border-r border-brand-border/60 max-h-[60vh] overflow-y-auto" data-id="element-634" data-mp="3ptfrdn6ruc">
                    {activeItem.items.map(subItem => {
                  const isActive = subItem.label === activeSub;
                  return <li key={`${activeItem.label}-${subItem.label}`} data-id="element-635" data-mp="nu277ivkjisu">
                          <a href={subItem.href} onMouseEnter={() => setActiveSub(subItem.children ? subItem.label : null)} onFocus={() => setActiveSub(subItem.children ? subItem.label : null)} className={`flex items-center justify-between gap-3 px-6 py-2.5 text-sm transition-colors focus:outline-none focus:bg-brand-panel focus:text-brand-maroon ${isActive ? 'bg-brand-panel text-brand-maroon font-medium' : 'text-brand-muted hover:bg-brand-panel hover:text-brand-maroon'}`} aria-haspopup={subItem.children ? true : undefined} data-id="element-636" data-mp="itnxzxi8e2t8">
                            <span className="min-w-0" data-id="element-637" data-mp="21r0y8o2139j4">{subItem.label}</span>
                            {subItem.children && <ChevronRight size={14} className="shrink-0 text-brand-muted" aria-hidden="true" data-id="element-638" data-mp="26pvsqn9cymj2" />}
                          </a>
                        </li>;
                })}
                  </ul>

                  <div className="p-5 bg-brand-panel/40 max-h-[60vh] overflow-y-auto" data-id="element-639" data-mp="2j87vh3ohf309">
                    {activeChildren.length > 0 ? <>
                        <p className="text-xs font-bold uppercase tracking-[0.08em] text-brand-muted mb-3" data-id="element-640" data-mp="2systoq08u5in">
                          {activeSub}
                        </p>
                        <ul className="grid grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-1" data-id="element-641" data-mp="20an67vht22f4">
                          {activeChildren.map(child => <li key={`${activeSub}-${child.label}`} data-id="element-642" data-mp="1vegb6xqy6bby">
                              <a href={child.href} className="block py-1.5 text-sm text-brand-muted hover:text-brand-maroon transition-colors focus:outline-none focus:text-brand-maroon" data-id="element-643" data-mp="2aae323tu8tid">
                                {child.label}
                              </a>
                              {child.children && <ul className="mb-1 ml-3 border-l border-brand-border pl-3" data-id="element-644" data-mp="3o7mp5b079hl7">
                                  {child.children.map(grandChild => <li key={`${child.label}-${grandChild.label}`} data-id="element-645" data-mp="3f9avf041e3oj">
                                      <a href={grandChild.href} className="block py-1 text-[13px] text-brand-muted/90 hover:text-brand-maroon transition-colors focus:outline-none focus:text-brand-maroon" data-id="element-646" data-mp="3jbxsbffkeesl">
                                        {grandChild.label}
                                      </a>
                                    </li>)}
                                </ul>}
                            </li>)}
                        </ul>
                      </> : <p className="text-sm text-brand-muted" data-id="element-647" data-mp="2v4312yu7t63v">
                        Hover a category to see its subcategories, or open{' '}
                        <a href={activeItem.href} className="text-brand-maroon underline underline-offset-2" data-id="element-648" data-mp="2gef0y0xrsdhq">
                          all {activeItem.fullLabel}
                        </a>
                        .
                      </p>}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </nav>
    </>;
}